import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from 'react';
import { useDispatch } from 'react-redux';
import classnames from 'classnames';
import NodePill from 'components/NodePillNew';
import ButtonNew from 'components/inputs/ButtonNew';
import './NodeSettingsConnections.scss';
import Icon from './Icon';
import { SettingsDropdown } from 'components/SettingsNew';
import {
  addConnection as addNodeConnection,
  deleteConnection as removeNodeConnection,
  updateConnection as updateNodeConnection,
} from 'actions/nodeSettings';

const NodeConnection = ({
  connection,
  matrix,
  removeConnection,
  isStatic,
  updateConnection,
}) => {
  const strength = useMemo(() => connection?.strength, [connection]);

  const fromNode = useMemo(() => matrix.nodes[connection.fromNode], [
    matrix,
    connection,
  ]);
  const toNode = useMemo(() => matrix.nodes[connection.toNode], [
    matrix,
    connection,
  ]);

  const [inputValue, setInputValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const handleChange = e => {
    const val = e.target.value;
    // Allow: numbers, minus sign, decimal point
    if (val === '' || val === '-' || !isNaN(val)) {
      setInputValue(val);
    }
  };

  const handleFocus = () => {
    setInputFocused(true);
    setInputValue(strength ?? '');
  };

  const handleBlur = () => {
    setInputFocused(false);
    // Parse to number when user leaves the field
    const parsed = parseFloat(inputValue);
    if (!isNaN(parsed)) {
      // onValueChange(parsed); // pass the real number up
      const oldConnection = {
        nodeFrom: connection?.fromNode,
        nodeTo: connection?.toNode,
        value: connection?.strength,
      };
      const nextConnection = {
        nodeFrom: fromNode?.id,
        nodeTo: toNode?.id,
        value: Math.min(1, Math.max(-1, Number(parsed))),
      };

      updateConnection(oldConnection, nextConnection);
      setInputValue('');
    }
  };

  return (
    <div className={classnames('NodeConnection')}>
      <div className="NodeConnection-connection">
        <div className="NodeConnection-pill-container">
          <NodePill node={fromNode} />
        </div>
        <div
          className={classnames('NodeConnection-arrow-icon-container', {
            decrease: connection?.strength < 0,
            increase: connection?.strength > 0,
          })}
        >
          <Icon icon="connectionsArrow" />
        </div>
        <div className="NodeConnection-pill-container">
          <NodePill node={toNode} />
        </div>
        by
        {isStatic ? (
          <span
            className={classnames('strength-value', {
              decrease: connection?.strength < 0,
              increase: connection?.strength > 0,
            })}
          >
            {connection.strength}
          </span>
        ) : (
          <input
            className={classnames('strength-value', {
              decrease: (inputFocused ? Number(inputValue) : strength) < 0,
              increase: (inputFocused ? Number(inputValue) : strength) > 0,
            })}
            type="number"
            value={inputFocused ? inputValue : strength}
            min={-1}
            max={1}
            step={0.01}
            onChange={handleChange}
            onBlur={handleBlur}
            onFocus={handleFocus}
          />
        )}
      </div>
      {isStatic ? null : (
        <div className="NodeConnection-buttons">
          <ButtonNew
            icon="trash"
            onClick={removeConnection}
            buttonClasses="icon-button-extra-small"
          />
        </div>
      )}
    </div>
  );
};

const NodeConnectionAdd = ({ connection, matrix, addConnection }) => {
  const [strength, setStrength] = useState(connection?.strength);

  const fromNode = useMemo(() => matrix.nodes[connection.fromNode] ?? null, [
    matrix,
    connection,
  ]);
  const toNode = useMemo(() => matrix.nodes[connection.toNode] ?? null, [
    matrix,
    connection,
  ]);

  const [selectedNode, setSelectedNode] = useState(null);

  const onAddConnection = useCallback(() => {
    const updatedNode = Object.values(matrix?.nodes).find(
      n => n?.id === selectedNode
    );
    const fromNodeId = fromNode ? fromNode?.id : updatedNode?.id;
    const toNodeId = toNode ? toNode?.id : updatedNode?.id;

    const nextConnection = { fromNode: fromNodeId, toNode: toNodeId, strength };

    addConnection(nextConnection);
    setSelectedNode(null);
    setStrength(connection?.strength);
  }, [
    matrix,
    selectedNode,
    fromNode,
    toNode,
    strength,
    addConnection,
    connection,
  ]);

  // TODO Dropdown is set up in a bad way to not use the value field and
  // only the label field. This needs to be fixed throughout the app
  const dropdownFromOptions = useMemo(() => {
    let options = Object.entries(matrix.nodes)
      .map(([nodeId, node]) => {
        if (matrix?.nodes?.[nodeId]?.influences?.[toNode?.id]) {
          return false;
        }
        return {
          value: node?.id,
          label: node?.name,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [matrix, toNode]);

  const dropdownToOptions = useMemo(() => {
    let options = Object.entries(matrix.nodes)
      .map(([nodeId, node]) => {
        if (matrix?.nodes?.[fromNode?.id]?.influences?.[nodeId]) {
          return false;
        }
        return {
          value: node?.id,
          label: node?.name,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));

    return options;
  }, [matrix, fromNode]);

  // ------------------------------------------------------------

  const [inputValue, setInputValue] = useState('');
  const [inputFocused, setInputFocused] = useState(false);

  const handleChange = e => {
    const val = e.target.value;
    // Allow: numbers, minus sign, decimal point
    if (val === '' || val === '-' || !isNaN(val)) {
      setInputValue(val);
    }
  };

  const handleFocus = () => {
    setInputFocused(true);
    setInputValue(strength ?? '');
  };

  const handleBlur = () => {
    setInputFocused(false);
    // Parse to number when user leaves the field
    const parsed = parseFloat(inputValue);
    if (!isNaN(parsed)) {
      // onValueChange(parsed); // pass the real number up

      setStrength(Math.min(1, Math.max(-1, Number(parsed))));
      setInputValue('');
    }
  };

  return (
    <div className={classnames('NodeConnection')}>
      <div className="NodeConnection-connection">
        <div className="NodeConnection-pill-container">
          {fromNode ? (
            <NodePill node={fromNode} />
          ) : (
            <SettingsDropdown
              onChange={v => setSelectedNode(v)}
              options={dropdownFromOptions}
              value={selectedNode}
              placeholder={'Select a node'}
            />
          )}
        </div>
        <div
          className={classnames('NodeConnection-arrow-icon-container', {
            decrease: connection?.strength < 0,
            increase: connection?.strength > 0,
          })}
        >
          <Icon icon="connectionsArrow" />
        </div>
        <div className="NodeConnection-pill-container">
          {toNode ? (
            <NodePill node={toNode} />
          ) : (
            <SettingsDropdown
              onChange={v => setSelectedNode(v)}
              options={dropdownToOptions}
              value={selectedNode}
              placeholder={'Select a node'}
            />
          )}
        </div>
        by
        <input
          className={classnames('strength-value', {
            decrease: (inputFocused ? Number(inputValue) : strength) < 0,
            increase: (inputFocused ? Number(inputValue) : strength) > 0,
          })}
          type="number"
          value={inputFocused ? inputValue : strength}
          min={-1}
          max={1}
          step={0.01}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
        />
      </div>
      <div className="NodeConnection-buttons">
        <ButtonNew
          icon="plus"
          onClick={onAddConnection}
          buttonClasses={classnames('icon-button-small', {
            disabled: strength === 0 || !selectedNode,
          })}
        />
      </div>
    </div>
  );
};

const NodeSettingsConnections = ({
  matrix,
  nodeId,
  activeConnection,
  isStatic,
}) => {
  const dispatch = useDispatch();

  const connectionsFrom = useMemo(() => {
    let from = matrix?.nodes?.[nodeId]?.influences;
    from = Object.entries(from).map(([k, v]) => ({
      fromNode: nodeId,
      toNode: k,
      strength: v,
    }));
    return from;
  }, [matrix, nodeId]);

  const connectionsTo = useMemo(() => {
    let to = Object.entries(matrix?.nodes).reduce((acc, [id, body]) => {
      for (const entry of Object.entries(body?.influences)) {
        const [toNode, strength] = entry;
        if (toNode === nodeId) {
          acc[id] = strength;
        }
      }
      return acc;
    }, {});

    to = Object.entries(to).map(([k, v]) => ({
      fromNode: k,
      toNode: nodeId,
      strength: v,
    }));
    return to;
  }, [matrix, nodeId]);

  const removeConnection = connection => {
    dispatch(removeNodeConnection(connection?.fromNode, connection?.toNode));
  };

  const addConnection = connection => {
    dispatch(
      addNodeConnection(
        connection?.fromNode,
        connection?.toNode,
        connection.strength
      )
    );
  };

  const updateConnection = (oldConnection, nextConnection) => {
    dispatch(updateNodeConnection(oldConnection, nextConnection));
  };

  return (
    <div className="NodeSettingsConnections">
      <div className="NodeSettingsConnections-column">
        <div className="NodeSettingsConnections-scroll-container">
          {connectionsFrom.map(connection => (
            <NodeConnection
              key={`${connection.fromNode}-${connection.toNode}`}
              connection={connection}
              matrix={matrix}
              updateConnection={updateConnection}
              removeConnection={() => removeConnection(connection)}
              isStatic={isStatic}
            />
          ))}
          {!isStatic ? (
            <NodeConnectionAdd
              connection={{
                fromNode: nodeId,
                toNode: null,
                strength: 0,
              }}
              matrix={matrix}
              addConnection={v => addConnection(v)}
            />
          ) : null}{' '}
        </div>
      </div>
      <div className="NodeSettingsConnections-column">
        <div className="NodeSettingsConnections-scroll-container">
          {connectionsTo.map(connection => (
            <NodeConnection
              key={`${connection.fromNode}-${connection.toNode}`}
              connection={connection}
              matrix={matrix}
              updateConnection={updateConnection}
              removeConnection={() => removeConnection(connection)}
              isStatic={isStatic}
            />
          ))}
          {!isStatic ? (
            <NodeConnectionAdd
              connection={{
                fromNode: null,
                toNode: nodeId,
                strength: 0,
              }}
              matrix={matrix}
              addConnection={v => addConnection(v)}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default NodeSettingsConnections;
